import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Frown, Loader2, MessagesSquare, Network, Sparkles } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { PainKindBadge } from "@/components/domain/analytics";
import { PageHeader } from "@/components/domain/misc";
import { ErrorPanel, Page } from "@/components/layout/page";
import { Button } from "@/components/ui/button";
import { SessionLink } from "@/features/analytics-shared/links";
import { Stepper } from "@/features/analytics-shared/stepper";
import {
  DRAFT_STEPS,
  type Draft,
  type DraftStepId,
  EMPTY_DRAFT,
  draftFromFinding,
  draftFromPainPoint,
  draftFromSession,
  firstInvalidStep,
  median,
  stepErrors,
  toDraftBody,
  validateDraft,
} from "@/features/signals/draft-model";
import { CreateStep, DefineStep, ExamplesStep, MethodStep, RubricStep, ScopeStep } from "@/features/signals/new-signal-steps";
import { q, useDraftSignal } from "@/lib/api";

export const Route = createFileRoute("/_app/signals/studio/new")({
  validateSearch: z.object({
    /** Session to pick example turns from ("Create signal from this"). */
    fromSession: z.string().min(1).optional().catch(undefined),
    /** A conversation-path finding to start from. */
    finding: z.string().min(1).max(1000).optional().catch(undefined),
    /** Agent to scope the signal to (e.g. from a path finding for one agent). */
    agentId: z.string().min(1).optional().catch(undefined),
    /** Pain point to track ("Track as signal"). */
    fromPainPointId: z.string().min(1).optional().catch(undefined),
  }),
  component: NewSignalPage,
});

function NewSignalPage() {
  const { fromSession, finding, fromPainPointId, agentId } = Route.useSearch();
  const navigate = useNavigate();
  const [draft, setDraftState] = useState<Draft>(EMPTY_DRAFT);
  const setDraft = (fn: (d: Draft) => Draft) => setDraftState(fn);
  const [step, setStep] = useState<DraftStepId>("define");
  const [attempted, setAttempted] = useState<Set<DraftStepId>>(new Set());

  const pains = useQuery({ ...q.painPoints(), enabled: !!fromPainPointId });
  const session = useQuery({ ...q.session(fromSession ?? ""), enabled: !!fromSession });
  const defs = useQuery(q.signalDefinitions());
  const agents = useQuery(q.agents());
  const names = useMemo(() => new Map(agents.data?.map((a) => [a.id, a.name])), [agents.data]);
  const pain = fromPainPointId ? pains.data?.find((p) => p.id === fromPainPointId) : undefined;
  const draftSignal = useDraftSignal();

  // Pre-fill once from whichever source the user came from.
  const seeded = useRef<string | null>(null);
  useEffect(() => {
    const key = `${fromPainPointId ?? ""}|${fromSession ?? ""}|${finding ?? ""}|${agentId ?? ""}`;
    if (seeded.current === key) return;
    if (fromPainPointId && !pain && pains.isPending) return;
    if (fromSession && !session.data && !session.error) return;
    seeded.current = key;
    const patch: Partial<Draft> = {
      ...(agentId ? { agentIds: [agentId] } : {}),
      ...(finding ? draftFromFinding(finding) : {}),
      ...(session.data ? draftFromSession(session.data) : {}),
      ...(pain ? draftFromPainPoint(pain) : {}),
    };
    setDraftState({ ...EMPTY_DRAFT, ...patch, rubric: patch.description ?? "", rubricEdited: false });
  }, [fromPainPointId, fromSession, finding, agentId, pain, pains.isPending, session.data, session.error]);

  const errors = useMemo(() => validateDraft(draft), [draft]);
  const blocker = firstInvalidStep(errors);
  const index = DRAFT_STEPS.findIndex((s) => s.id === step);
  const maxReachable = blocker >= 0 ? blocker : DRAFT_STEPS.length - 1;
  const shown = attempted.has(step) ? stepErrors(step, errors) : {};
  const estCost = useMemo(() => {
    const xs = (defs.data ?? []).filter((d) => d.method === draft.method).map((d) => d.costPer1k);
    return xs.length ? median(xs) : null;
  }, [defs.data, draft.method]);

  const next = () => {
    setAttempted((a) => new Set(a).add(step));
    if (Object.keys(stepErrors(step, errors)).length) {
      requestAnimationFrame(() => document.querySelector<HTMLElement>("[aria-invalid='true']")?.focus());
      return;
    }
    const n = DRAFT_STEPS[index + 1];
    if (n) setStep(n.id);
  };
  const back = () => {
    const p = DRAFT_STEPS[index - 1];
    if (p) setStep(p.id);
  };
  const create = () => {
    if (blocker >= 0) {
      setStep(DRAFT_STEPS[blocker]!.id);
      setAttempted(new Set(DRAFT_STEPS.map((s) => s.id)));
      return;
    }
    draftSignal.mutate(toDraftBody(draft, pain?.id), {
      onSuccess: (d) => void navigate({ to: "/signals/studio/$definitionId", params: { definitionId: d.id }, replace: true }),
    });
  };

  let source: ReactNode = null;
  let sourceLabel: string | null = null;
  if (fromPainPointId) {
    sourceLabel = pain ? `Pain point · ${pain.title}` : "Pain point";
    source = pains.error ? (
      <ErrorPanel error={pains.error} reset={() => void pains.refetch()} className="py-6" />
    ) : (
      <SourceNote icon={<Frown />}>
        {pain ? (
          <>
            Tracking pain point <span className="font-medium text-fg">{pain.title}</span> <PainKindBadge kind={pain.kind} />. Its quotes are pre-filled as examples and it moves to <em>tracking</em> once the signal is created.
          </>
        ) : pains.isPending ? (
          "Loading the pain point…"
        ) : (
          <>Pain point {fromPainPointId} wasn't found (it may be dismissed). You can still write the signal.</>
        )}
      </SourceNote>
    );
  } else if (fromSession) {
    sourceLabel = `Session ${fromSession}`;
    source = session.error ? (
      <ErrorPanel error={session.error} reset={() => void session.refetch()} className="py-6" />
    ) : (
      <SourceNote icon={<MessagesSquare />}>
        Starting from session <SessionLink id={fromSession} />
        {session.data ? <> on <span className="font-mono text-[12px]">{session.data.agentName}</span>: {session.data.intent}</> : null}. You'll pick example turns from it in step 3.
      </SourceNote>
    );
  } else if (finding) {
    sourceLabel = "Conversation-path finding";
    source = (
      <SourceNote icon={<Network />}>
        Starting from a conversation-path finding: <span className="text-fg">“{finding}”</span>
      </SourceNote>
    );
  }

  return (
    <Page>
      <PageHeader
        breadcrumb={
          <nav aria-label="Breadcrumb">
            <Link to="/signals" className="hover:text-fg">
              Signals
            </Link>
            <span className="px-1.5">/</span>
            <Link to="/signals/studio" className="hover:text-fg">
              Studio
            </Link>
            <span className="px-1.5">/</span>
            <span className="text-fg-2">New signal</span>
          </nav>
        }
        title="New signal"
        description="Define a signal in plain language. It's an analytics signal: it drives dashboards, alerts and optional issues, and never moves your price."
        actions={
          <Button variant="ghost" size="sm" asChild>
            <Link to="/signals/studio">Cancel</Link>
          </Button>
        }
      />

      <Stepper steps={[...DRAFT_STEPS]} current={step} maxReachable={maxReachable} onSelect={(s) => setStep(s)} label="New signal steps" />

      <div className="mt-4 flex max-w-4xl flex-col gap-4">
        {step === "define" ? <DefineStep draft={draft} setDraft={setDraft} errors={shown} source={source} /> : null}
        {step === "rubric" ? <RubricStep draft={draft} setDraft={setDraft} errors={shown} /> : null}
        {step === "examples" ? <ExamplesStep draft={draft} setDraft={setDraft} errors={shown} session={session.data} sessionLoading={!!fromSession && session.isPending} /> : null}
        {step === "method" ? <MethodStep draft={draft} setDraft={setDraft} defs={defs.data} errors={shown} /> : null}
        {step === "scope" ? <ScopeStep draft={draft} setDraft={setDraft} errors={shown} /> : null}
        {step === "create" ? <CreateStep draft={draft} names={names} estCost={estCost} sourceLabel={sourceLabel} /> : null}

        <div className="flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={back} disabled={index === 0}>
            <ArrowLeft /> Back
          </Button>
          <div className="flex items-center gap-3">
            {Object.keys(shown).length ? (
              <span role="alert" className="text-xs text-critical-text">
                Fix the highlighted fields to continue.
              </span>
            ) : null}
            {step === "create" ? (
              <Button onClick={create} disabled={draftSignal.isPending}>
                {draftSignal.isPending ? <Loader2 className="animate-spin" /> : <Sparkles />} Create and backtest
              </Button>
            ) : (
              <Button onClick={next}>
                Continue <ArrowRight />
              </Button>
            )}
          </div>
        </div>
      </div>
    </Page>
  );
}

function SourceNote({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-dashed bg-surface-2/60 px-3 py-2 text-xs text-fg-2 [&>svg]:mt-px [&>svg]:size-3.5 [&>svg]:shrink-0 [&>svg]:text-fg-3">
      {icon}
      <span>{children}</span>
    </div>
  );
}
